// src/pages/RegistrationForm.jsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import AuthService from "../services/AuthService";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import { getCroppedImg } from "../utils/cropImage";
import Select from "react-select";
import { getAllDepartments } from "../services/departmentService";

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    ServiceNo: "",
    Name: "",
    MobileNo: "",
    role: "E",
    status: "A",
    department_code: "",
    password: "",
    confirmPassword: "",
  });

  const [localError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile Image
  const [profileImg, setProfileImg] = useState(null); // final cropped image blob
  const [previewImg, setPreviewImg] = useState(null); // preview URL
  const [selectedFile, setSelectedFile] = useState(null); // raw file for cropping

  // Cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  const navigate = useNavigate();

  const roles = [
    { value: "A", label: "Admin" },
    { value: "S", label: "Supervisor" },
    { value: "E", label: "Employee" },
    { value: "EN", label: "Engineer" },
    { value: "D", label: "Driver" },
  ];

  const [departments, setDepartments] = useState([]);
  const departmentOptions = useMemo(() => departments.map((d) => ({ value: d.code, label: d.name })), [departments]);

  useEffect(() => {
    const loadDept = async () => {
      try {
        const data = await getAllDepartments();
        setDepartments(data);
      } catch (err) {
        console.error("Error loading departments", err);
      }
    };
    loadDept();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Input restrictions
    if (name === "MobileNo") {
      // Only allow digits and max 10 characters
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    if (name === "ServiceNo") {
      // Only allow digits and max 7 characters
      if (!/^\d*$/.test(value)) return;
      if (value.length > 7) return;
    }

    if (name === "Name") {
      // Only allow alphabetic characters and spaces
      if (!/^[a-zA-Z\s]*$/.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setLocalError("");
  };

  const handleDeptSelect = (option) => {
    setFormData((prev) => ({ ...prev, department_code: option ? option.value : "" }));
  };

  // Handle image selection or re-edit
  const handleImageSelect = (e) => {
    const file = e.target?.files?.[0] || null;
    if (!file && !profileImg) return;

    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImg(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    } else if (profileImg) {
      // Re-edit existing cropped image
      setPreviewImg(URL.createObjectURL(profileImg));
      setShowCropModal(true);
    }
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const applyCrop = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(previewImg, croppedAreaPixels);
      setProfileImg(croppedImage);
      setPreviewImg(URL.createObjectURL(croppedImage));
      setShowCropModal(false);
    } catch (err) {
      console.error(err);
    }
  }, [croppedAreaPixels, previewImg]);

  const cancelCrop = () => {
    // If editing existing image, keep old image; if new selection, discard
    if (!profileImg) setPreviewImg(null);
    setShowCropModal(false);
    setSelectedFile(null);
  };

  const validateForm = () => {
    if (!formData.ServiceNo.trim()) {
      setLocalError("Service Number is required");
      return false;
    }
    if (!/^\d{7}$/.test(formData.ServiceNo)) {
      setLocalError("Service Number must be numeric and exactly 7 digits");
      return false;
    }
    if (!formData.Name.trim()) {
      setLocalError("Name is required");
      return false;
    }
    if (formData.Name.trim().length < 3) {
      setLocalError("Name must be at least 3 characters");
      return false;
    }
    if (!formData.MobileNo.trim()) {
      setLocalError("Mobile Number is required");
      return false;
    }
    if (!/^\d{10}$/.test(formData.MobileNo)) {
      setLocalError("Mobile Number must be 10 digits");
      return false;
    }
    if (!formData.password) {
      setLocalError("Password is required");
      return false;
    }
    if (formData.password.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    setSuccessMsg("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Check if user already exists
      const checkUser = await AuthService.GetUserDetailsByUser(formData.ServiceNo.trim());
      if (
        checkUser.data &&
        checkUser.data.statusCode === 200 &&
        checkUser.data.resultSet &&
        checkUser.data.resultSet.length > 0
      ) {
        setLocalError("This Service Number is already registered.");
        return;
      }
    } catch (err) {
      console.warn("User existence check failed:", err);
      // Proceed if check fails (e.g. network error or 404 if API returns 404 for not found)
    }

    try {
      // Prepare user data in JSON format as required by backend
      const userData = {
        ServiceNo: formData.ServiceNo.trim(),
        Name: formData.Name.trim(),
        MobileNo: formData.MobileNo.trim(),
        role: formData.role,
        status: formData.status,
        department_code: formData.department_code,
        password: formData.password,
        file_path: "",
        ImageFile: null, // Image uploaded separately if needed
      };

      // Send user details as JSON array to backend
      await axios.post(
        "https://aeroviewtestbackend.dockyardsoftware.com/UserRegister/PostUserDetails",
        [userData],
        { headers: { "Content-Type": "application/json" } }
      );

      // Upload profile image if available
      if (profileImg) {
        const formDataImg = new FormData();
        formDataImg.append("ServiceNo", formData.ServiceNo.trim());
        formDataImg.append("Name", formData.Name.trim());
        formDataImg.append("MobileNo", formData.MobileNo.trim());
        formDataImg.append("role", formData.role);
        formDataImg.append("status", formData.status);
        formDataImg.append("password", formData.password);
        formDataImg.append("file_path", "");
        const file = new File([profileImg], `${formData.ServiceNo.trim()}.jpg`, { type: "image/jpeg" });
        formDataImg.append("ImageFile", file);

        await axios.post(
          "https://aeroviewtestbackend.dockyardsoftware.com/User/Fileupload",
          formDataImg,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      setSuccessMsg("Registration successful! Redirecting to login...");

      // Reset form
      setFormData({
        ServiceNo: "",
        Name: "",
        MobileNo: "",
        role: "E",
        status: "A",
        department_code: "",
        password: "",
        confirmPassword: "",
      });
      setProfileImg(null);
      setPreviewImg(null);
      setSelectedFile(null);

      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      console.error(err);
      setLocalError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 px-4 py-8">
      <div className="bg-white border border-gray-300 rounded-lg w-full max-w-lg p-10 shadow-lg">

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-blue-900">User Registration</h1>
          <p className="text-gray-700 text-sm">Create a new user account</p>
        </div>

        {successMsg && (
          <div className="px-3 py-3 bg-green-50 border-l-4 border-green-500 rounded text-green-800 text-sm mb-5">{successMsg}</div>
        )}
        {localError && (
          <div className="px-3 py-3 bg-amber-50 border-l-4 border-red-500 rounded text-red-800 text-sm mb-5">{localError}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          <div>
            <label className="text-xs font-semibold text-gray-800">Service Number</label>
            <input
              type="text"
              name="ServiceNo"
              value={formData.ServiceNo}
              onChange={handleChange}
              placeholder="3000300"
              className="px-3 py-3 border border-gray-300 rounded w-full"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Full Name</label>
            <input
              type="text"
              name="Name"
              value={formData.Name}
              onChange={handleChange}
              placeholder="S.A.N.R Gunasekara"
              className="px-3 py-3 border border-gray-300 rounded w-full"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Mobile Number</label>
            <input
              type="tel"
              name="MobileNo"
              value={formData.MobileNo}
              onChange={handleChange}
              placeholder="0715510099"
              className="px-3 py-3 border border-gray-300 rounded w-full"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="px-3 py-3 border border-gray-300 rounded w-full bg-white"
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Department</label>
            <Select
              options={departmentOptions}
              value={departmentOptions.find((o) => o.value === formData.department_code) || null}
              onChange={handleDeptSelect}
              placeholder="-- Select Department --"
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Profile Picture</label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="px-2 py-2 border border-gray-300 rounded"
              />
              {profileImg && (
                <button
                  type="button"
                  onClick={handleImageSelect} // re-edit existing image
                  className="px-3 py-2 bg-blue-900 text-white rounded hover:bg-blue-800"
                >
                  Edit
                </button>
              )}
            </div>
            {previewImg && (
              <img
                src={previewImg}
                alt="Preview"
                className="mt-3 h-24 w-24 rounded-full border shadow object-cover"
              />
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              className={`px-3 py-3 border rounded w-full ${
                formData.password.length > 0 && formData.password.length < 8 
                  ? "border-red-500 focus:ring-red-500" 
                  : "border-gray-300"
              }`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-sm text-blue-700 mt-1">
              {showPassword ? "Hide" : "Show"}
            </button>
            {formData.password.length > 0 && (
              <p className={`text-xs mt-1 ${formData.password.length >= 8 ? "text-green-600" : "text-red-500"}`}>
                {formData.password.length >= 8 ? "✓ Password length is valid" : "• Password must be at least 8 characters"}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-800">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm password"
              className="px-3 py-3 border border-gray-300 rounded w-full"
            />
            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-sm text-blue-700 mt-1">
              {showConfirmPassword ? "Hide" : "Show"}
            </button>
          </div>

          <button type="submit" disabled={loading} className={`py-3 font-semibold rounded transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-900 text-white hover:bg-blue-800"}`}>
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <div className="text-center mt-6 pt-6 border-t border-gray-300">
          <p className="text-gray-700 text-sm">
            Already have an account? <Link to="/login" className="text-blue-600 font-semibold">Login here</Link>
          </p>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex justify-center items-center">
          <div className="bg-white rounded-lg p-4 w-80 h-96 relative flex flex-col">
            <div className="relative flex-1">
              <Cropper
                image={previewImg}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            </div>
            <div className="mt-2">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, val) => setZoom(val)}
              />
            </div>
            <div className="flex justify-between mt-3">
              <button
                type="button"
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={cancelCrop}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-900 text-white rounded hover:bg-blue-800"
                onClick={applyCrop}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
